/**
 * @voltxampere/spice — Op-amp codec (X subcircuit-as-component)
 *
 * Berkeley SPICE3 syntax: `X{name} VPLUS VMINUS VPP VNN VOUT MODEL`
 *
 * Examples:
 *   X1 vplus vminus vcc vee out OPAMP_IDEAL
 *   X2 in+ in- vcc vee out LM741
 *
 * Sprint 16 only handles known opamp subcircuit names. Anything
 * else surfaces a warning and falls back to OPAMP_IDEAL — Sprint
 * 22+ adds full subcircuit definition flattening.
 *
 * Schema mapping: `type: "op-amp"`, params: `{ model,
 * supplyVoltage? }`. Five terminals (V+, V-, Vsupply+, Vsupply-,
 * Vout) propagate through.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ok, err, } from "./_base.js";
import { stringParam } from "./_param-helpers.js";
const KNOWN_OPAMP_MODELS = new Set([
    "OPAMP_IDEAL",
    "IDEAL_OPAMP",
    "LM741",
    "LM358",
    "TL081",
    "TL082",
    "OP07",
    "OP27",
    "MCP6004",
]);
export const opampCodec = {
    spiceLetter: "X",
    schemaTypes: ["op-amp", "opamp"],
    examples: ["X1 vplus vminus vcc vee out OPAMP_IDEAL", "X2 in+ in- vcc vee out LM741"],
    parse(card, ctx) {
        if (card.tokens.length < 6) {
            return err("X needs 5 nodes and a model name (Sprint 16 = opamp only)");
        }
        const nodes = card.tokens.slice(0, 5);
        const modelRaw = card.tokens[5];
        if (modelRaw === undefined || nodes.length !== 5) {
            return err("X has incomplete tokens");
        }
        const upper = modelRaw.toUpperCase();
        let resolvedModel = modelRaw;
        if (!KNOWN_OPAMP_MODELS.has(upper)) {
            ctx.warnings.push(`${card.identifier}: unknown subcircuit '${modelRaw}' — defaulting to OPAMP_IDEAL (Sprint 22+ will support custom subcircuits)`);
            resolvedModel = "OPAMP_IDEAL";
        }
        const params = { model: resolvedModel };
        const component = {
            id: card.identifier,
            type: "op-amp",
            position: { x: 0, y: 0 },
            rotation: 0,
            flipped: false,
            label: card.identifier,
            params,
            terminals: [],
        };
        // Strip optional 'undefined' (we already validated length === 5).
        const safeNodes = nodes;
        return ok(component, safeNodes);
    },
    emit(component, terminals) {
        const model = stringParam(component.params.model, "OPAMP_IDEAL");
        const [vp, vm, vcc, vee, out] = terminals;
        return `${component.id} ${vp ?? "0"} ${vm ?? "0"} ${vcc ?? "0"} ${vee ?? "0"} ${out ?? "0"} ${model}`;
    },
    owns(schemaType) {
        return schemaType === "op-amp" || schemaType === "opamp";
    },
};
export { KNOWN_OPAMP_MODELS };
//# sourceMappingURL=opamp.js.map